import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Card, Input, cn } from '../../components/ui/Kit';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import SafeIcon from '../../common/SafeIcon';
import {
  slugify,
  getDefaultBlocksForStepType,
  getScratchSteps,
} from '../../lib/funnelUtils';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiLayers, FiEdit3 } = FiIcons;

const SCRATCH_ID = '__scratch__';

function buildStepsFromTemplate(template) {
  const json = template?.funnel_json ?? {};
  const steps = json.steps ?? [];
  return steps.map((s) => ({
    step_number: s.step_number,
    step_type: s.step_type,
    title: s.title,
    slug: s.slug,
    is_entry_point: s.is_entry_point ?? false,
    blocks: (s.blocks ?? []).map((b, i) => ({
      block_type: b.block_type,
      position: b.position ?? i,
      content: b.content ?? {},
      is_visible: b.is_visible ?? true,
    })),
  }));
}

function buildScratchSteps() {
  return getScratchSteps().map((s) => ({
    ...s,
    blocks: getDefaultBlocksForStepType(s.step_type),
  }));
}

export default function FunnelNew() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugError, setSlugError] = useState('');
  const [creating, setCreating] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.funnelTemplates.getAll();
      setTemplates(data ?? []);
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to load templates', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  useEffect(() => {
    if (!slugTouched && name) setSlug(slugify(name));
  }, [name, slugTouched]);

  useEffect(() => {
    if (!slug) { setSlugError(''); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const exists = await api.funnels.slugExists(slug);
        if (!cancelled) setSlugError(exists ? 'This slug is already taken' : '');
      } catch {
        if (!cancelled) setSlugError('');
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [slug]);

  const selectedTemplate = selectedId === SCRATCH_ID
    ? null
    : templates.find((t) => t.id === selectedId);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      addToast({ type: 'error', title: 'Name is required' });
      return;
    }
    if (!slug.trim() || slugError) {
      addToast({ type: 'error', title: 'Valid slug is required' });
      return;
    }

    setCreating(true);
    try {
      const steps = selectedTemplate
        ? buildStepsFromTemplate(selectedTemplate)
        : buildScratchSteps();

      const funnel = await api.funnels.create({
        agency_id: user.agency_id,
        name: name.trim(),
        slug: slug.trim(),
        template_id: selectedTemplate?.id ?? null,
        steps,
      });

      addToast({ type: 'success', title: 'Funnel created' });
      navigate(`/funnels/${funnel.id}`);
    } catch (err) {
      addToast({ type: 'error', title: 'Create failed', message: err.message });
    } finally {
      setCreating(false);
    }
  };

  const systemTemplates = templates.filter((t) => t.is_system);
  const agencyTemplates = templates.filter((t) => !t.is_system);

  const TemplateCard = ({ template, isSelected, onSelect }) => (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'text-left rounded-lg border-2 p-4 transition-all hover:shadow-md w-full',
        isSelected ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'border-gray-200 bg-white hover:border-gray-300',
      )}
    >
      {template.thumbnail_url ? (
        <img src={template.thumbnail_url} alt="" className="w-full h-28 object-cover rounded-md mb-3 bg-gray-100" />
      ) : (
        <div className="w-full h-28 rounded-md mb-3 bg-gradient-to-br from-sidebar to-primary/30 flex items-center justify-center">
          <SafeIcon icon={FiLayers} className="w-10 h-10 text-white/80" />
        </div>
      )}
      <p className="font-semibold text-gray-900">{template.name}</p>
      {template.description && (
        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{template.description}</p>
      )}
      <p className="text-xs text-gray-400 mt-2">{template.step_count ?? 0} step(s)</p>
    </button>
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Spinner size="lg" className="text-primary" />
        <p className="text-sm text-gray-500">Loading templates…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/funnels')}>
          <SafeIcon icon={FiArrowLeft} className="mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Funnel</h1>
          <p className="text-sm text-gray-500">Pick a template or start from scratch.</p>
        </div>
      </div>

      {!selectedId ? (
        <>
          <div>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Start from scratch</h2>
            <TemplateCard
              template={{ name: 'Blank Funnel', description: '3 default steps: Opt-in, Watch, Book', step_count: 3 }}
              isSelected={false}
              onSelect={() => setSelectedId(SCRATCH_ID)}
            />
          </div>

          {systemTemplates.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">System templates</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {systemTemplates.map((t) => (
                  <TemplateCard
                    key={t.id}
                    template={t}
                    isSelected={selectedId === t.id}
                    onSelect={() => setSelectedId(t.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {agencyTemplates.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Agency templates</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {agencyTemplates.map((t) => (
                  <TemplateCard
                    key={t.id}
                    template={t}
                    isSelected={selectedId === t.id}
                    onSelect={() => setSelectedId(t.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {templates.length === 0 && (
            <EmptyState
              icon={FiLayers}
              title="No saved templates"
              description="System templates will appear here once seeded. You can still start from scratch."
            />
          )}
        </>
      ) : (
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
            <SafeIcon icon={FiEdit3} className="w-4 h-4 text-primary" />
            <span>
              Using: <strong>{selectedId === SCRATCH_ID ? 'Blank Funnel' : selectedTemplate?.name}</strong>
            </span>
            <button type="button" className="ml-auto text-primary text-sm hover:underline" onClick={() => setSelectedId(null)}>
              Change template
            </button>
          </div>

          <form onSubmit={handleCreate} className="space-y-4">
            <Input
              label="Funnel name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Recruiting VSL Funnel"
              required
            />
            <Input
              label="URL slug"
              value={slug}
              onChange={(e) => { setSlug(slugify(e.target.value)); setSlugTouched(true); }}
              placeholder="recruiting-vsl"
              error={slugError}
            />
            {slug && !slugError && (
              <p className="text-xs text-gray-500">
                Live URL:{' '}
                <span className="font-mono text-primary">go.prosperityleaders.net/{slug}</span>
              </p>
            )}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <Button type="button" variant="outline" onClick={() => setSelectedId(null)}>Back</Button>
              <Button type="submit" isLoading={creating} disabled={Boolean(slugError)}>
                Create Funnel
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
